import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Play,
  Dumbbell,
  History,
  Calendar,
} from 'lucide-react';
import { programsApi, exercisesApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatDate } from '@/lib/utils';
import type { ProgramExercise, LastWeightsResponse } from '@/types';

export function StartWorkoutPage() {
  const { programId, templateId } = useParams<{ programId: string; templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Hae pohja
  const { data: template, isLoading: loadingTemplate } = useQuery({
    queryKey: ['template', programId, templateId],
    queryFn: () => programsApi.getTemplate(programId!, templateId!),
    enabled: !!programId && !!templateId,
  });

  // Hae ohjelma
  const { data: program } = useQuery({
    queryKey: ['program', programId],
    queryFn: () => programsApi.get(programId!),
    enabled: !!programId,
  });

  // Hae viimeisimmät painot kaikille liikkeille
  const exerciseIds = template?.exercises?.map((e) => e.exerciseId) || [];

  const lastWeightsQueries = useQuery({
    queryKey: ['lastWeights', exerciseIds],
    queryFn: async () => {
      const results: Record<string, LastWeightsResponse> = {};
      for (const exerciseId of exerciseIds) {
        try {
          const data = await exercisesApi.getLastWeights(exerciseId);
          results[exerciseId] = data;
        } catch {
          // Ignore errors for individual exercises
        }
      }
      return results;
    },
    enabled: exerciseIds.length > 0,
  });

  // Aloita treeni pohjasta
  const startWorkoutMutation = useMutation({
    mutationFn: () => programsApi.startWorkout(programId!, templateId!),
    onSuccess: (workout) => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      // Siirry suoraan treenin muokkaukseen
      navigate(`/workouts/live/${workout.id}`);
    },
  });

  const handleStartWorkout = () => {
    startWorkoutMutation.mutate();
  };

  if (loadingTemplate) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Treenipohjaa ei löytynyt</p>
        <Link to={`/programs/${programId}`} className="btn btn-secondary mt-4">
          Takaisin ohjelmaan
        </Link>
      </div>
    );
  }

  const exercises = template.exercises || [];
  const lastWeights = lastWeightsQueries.data || {};

  return (
    <div className="max-w-2xl mx-auto">
      {/* Navigaatio */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/programs/${programId}`)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <p className="text-sm text-gray-500">{program?.name}</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Aloita treeni
          </h1>
        </div>
      </div>

      {/* Pohjan tiedot */}
      <div className="card p-5 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {template.name}
        </h2>
        {template.notes && (
          <p className="text-gray-600 dark:text-gray-400 mb-4">{template.notes}</p>
        )}

        <div className="flex items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1.5">
            <Dumbbell className="h-4 w-4" />
            <span>
              {exercises.length} {exercises.length === 1 ? 'liike' : 'liikettä'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span>
              {exercises.reduce((sum, e) => sum + (e.sets?.length || 0), 0)} sarjaa yhteensä
            </span>
          </div>
        </div>
      </div>

      {/* Liikkeet ja edelliset painot */}
      <div className="card p-5 mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Dumbbell className="h-5 w-5 text-primary-500" />
          Liikkeet ja edelliset painot
        </h3>

        {exercises.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <p>Pohjassa ei ole liikkeitä</p>
            <Link
              to={`/programs/${programId}/templates/${templateId}`}
              className="text-primary-600 hover:underline mt-2 inline-block"
            >
              Lisää liikkeitä pohjaan
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {exercises.map((exercise: ProgramExercise, index: number) => {
              const lastWeight = lastWeights[exercise.exerciseId];
              const lastWorkout = lastWeight?.lastWorkout;
              const hasLastWeights = (lastWorkout?.sets?.length ?? 0) > 0;

              return (
                <div
                  key={exercise.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  {/* Liikkeen nimi ja sarjat */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">#{index + 1}</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {exercise.exercise?.name || 'Tuntematon'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {exercise.sets?.length || 0} sarjaa
                        {exercise.sets?.length > 0 && (
                          <>
                            {' '}
                            ×{' '}
                            {exercise.sets.map((s) => s.targetReps || '?').join('/')} toistoa
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Edelliset painot */}
                  {hasLastWeights && lastWorkout ? (
                    <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2 text-sm text-primary-700 dark:text-primary-300 mb-2">
                        <History className="h-4 w-4" />
                        <span className="font-medium">Viime kerta</span>
                        <span className="text-primary-500 dark:text-primary-400 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(lastWorkout.date)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {lastWorkout.sets.map((set, setIndex) => (
                          <span
                            key={setIndex}
                            className="inline-flex items-center px-2 py-1 bg-white dark:bg-gray-800 rounded-sm text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            {set.weight} kg × {set.reps ?? '?'}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : lastWeightsQueries.isLoading ? (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <LoadingSpinner size="sm" />
                        <span>Ladataan edellisiä painoja...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <History className="h-4 w-4" />
                        <span>Ei aiempia painoja tällä liikkeellä</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Aloita treeni -nappi */}
      <div className="flex gap-3">
        <button
          onClick={() => navigate(`/programs/${programId}`)}
          className="btn btn-secondary flex-1"
        >
          Peruuta
        </button>
        <button
          onClick={handleStartWorkout}
          disabled={startWorkoutMutation.isPending || exercises.length === 0}
          className="btn btn-primary flex-1"
        >
          {startWorkoutMutation.isPending ? (
            <LoadingSpinner size="sm" className="text-white" />
          ) : (
            <>
              <Play className="h-5 w-5" />
              Aloita treeni
            </>
          )}
        </button>
      </div>

      {/* Info */}
      <p className="text-center text-sm text-gray-500 mt-4">
        Treeni luodaan automaattisesti pohjan perusteella. Voit täyttää painot treenin aikana.
      </p>
    </div>
  );
}
